import styled from "styled-components";
import type { SpeakerProfile } from "../../lib/types.ts";

type SpeakerSelectProps = {
  speakers: number[];
  profiles: Map<number, SpeakerProfile>;
  value: number;
  onChange: (speakerIndex: number) => void;
  disabled?: boolean;
};

/**
 * Construye la etiqueta visible para un speaker.
 *
 * Si el speaker tiene perfil con nombre → "Pedro (Manager)"
 * Si no tiene perfil → "Speaker N" (para detectados) o "Speaker manual" (custom)
 */
function speakerLabel(
  index: number,
  profile: SpeakerProfile | undefined,
): string {
  if (profile && profile.name.trim()) {
    return profile.role.trim()
      ? `${profile.name} (${profile.role})`
      : profile.name;
  }
  if (index < 0) return `Speaker manual ${Math.abs(index)} (sin nombre)`;
  return `Speaker ${index} (sin nombre)`;
}

export function SpeakerSelect({
  speakers,
  profiles,
  value,
  onChange,
  disabled,
}: SpeakerSelectProps) {
  return (
    <Wrapper>
      <StyledSelect
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
      >
        {speakers.map((sp) => (
          <option key={sp} value={sp}>
            {speakerLabel(sp, profiles.get(sp))}
          </option>
        ))}
      </StyledSelect>
      <Caret>▾</Caret>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  min-width: 240px;
`;

const StyledSelect = styled.select`
  width: 100%;
  appearance: none;
  font-family: inherit;
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.borderStrong};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) =>
    `${theme.space.sm} ${theme.space.lg} ${theme.space.sm} ${theme.space.md}`};
  cursor: pointer;
  transition: border-color 120ms, box-shadow 120ms;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.color.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.accent};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.accentMuted};
  }

  &:disabled {
    background: ${({ theme }) => theme.color.background};
    cursor: not-allowed;
  }
`;

const Caret = styled.span`
  position: absolute;
  right: ${({ theme }) => theme.space.sm};
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.xs};
`;
